/**
 * „Ich bin der Veranstalter" — der Weg dorthin, an einer Stelle.
 *
 * Der Antrag selbst wird auf der Veranstalterseite gestellt (Dialog in `OrganizerDetail`), der
 * Hinweis steht aber an zwei Orten: dort und in der Veranstalter-Karte einer Event-Seite. Beide
 * müssen einen nicht angemeldeten Besucher gleich behandeln — sonst führt der eine Weg zum
 * Antrag und der andere ins Leere.
 */

/**
 * Merkt sich die Absicht über Anmeldung und Registrierung hinweg.
 *
 * Der Rückweg allein reicht nicht: nach einer Registrierung führt der Weg über die
 * Bestätigungsmail und landet irgendwo — die Absicht wäre unterwegs verloren, und der Antrag,
 * für den jemand extra ein Konto angelegt hat, wäre nie gestellt worden.
 */
export const CLAIM_INTENT_KEY = 'radevents.claimIntent';

/** Freemail-Domains sind kein Besitznachweis; gespiegelt in OrganizerMember.kt. */
const FREEMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'web.de', 'gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch', 't-online.de',
  'outlook.de', 'outlook.com', 'hotmail.de', 'hotmail.com', 'live.de', 'live.com', 'yahoo.de',
  'yahoo.com', 'icloud.com', 'me.com', 'mac.com', 'freenet.de', 'posteo.de', 'mail.de', 'aol.com',
  'aol.de', 'arcor.de', 'online.de', '1und1.de', 'magenta.de', 'bluewin.ch', 'aon.at', 'a1.net',
  'chello.at', 'protonmail.com', 'proton.me', 'mailbox.org',
]);

const mailDomainOf = (email?: string): string | null => {
  const domain = email?.split('@')[1]?.trim().toLowerCase();
  return domain?.includes('.') ? domain : null;
};

/**
 * Die Domain der hinterlegten Website, ohne Schema, Port und `www.`.
 *
 * Dieselbe Regel wie im Backend (`emailDomainMatchesWebsite`), hier nur als Hilfestellung: sie
 * entscheidet nichts, sie sagt dem Antragsteller nur, ob er sich Arbeit sparen kann.
 */
export const claimDomainOf = (website?: string): string | null => {
  const host = website?.trim().toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .split('/')[0]
    .split(':')[0]
    .replace(/^www\./, '');
  return host && host.includes('.') ? host : null;
};

/** Dieselbe Vorschauentscheidung wie im Backend: bestätigt, passend und keine Freemail. */
export const organizerDomainMatches = (
  userEmail?: string,
  emailVerified?: boolean,
  website?: string,
  organizerEmail?: string,
): boolean | null => {
  const domain = mailDomainOf(userEmail);
  if (!domain || !emailVerified) return null;
  const candidates = [claimDomainOf(website), mailDomainOf(organizerEmail)].filter(
    (candidate): candidate is string => !!candidate,
  );
  if (candidates.length === 0) return null;
  // Dieselbe Adresse zählt immer, auch bei Freemail — gespiegelt aus
  // `emailDomainMatchesOrganizerEmail`: der Ausschluss gilt der geteilten Domain, nicht der
  // Adresse selbst.
  if (organizerEmail && userEmail
    && userEmail.trim().toLowerCase() === organizerEmail.trim().toLowerCase()) return true;
  if (FREEMAIL_DOMAINS.has(domain)) return false;
  return candidates.some(candidate => !FREEMAIL_DOMAINS.has(candidate)
    && (domain === candidate || domain.endsWith(`.${candidate}`) || candidate.endsWith(`.${domain}`)));
};

/** Die Seite, auf der der Antrag gestellt wird — mit geöffnetem Dialog. */
export const claimPath = (organizerId: number, offer?: string): string =>
  `/veranstalter/${organizerId}?antrag=1${offer ? `&angebot=${encodeURIComponent(offer)}` : ''}`;

/**
 * Der Einmal-Link aus der Outreach-Mail, gemerkt über Anmeldung und Registrierung hinweg.
 *
 * Getrennt von [CLAIM_INTENT_KEY], weil beide verschiedene Dinge bedeuten: die Absicht öffnet
 * den Antrag, der Token schaltet ohne Antrag frei. Wer beides im selben Schlüssel ablegte,
 * müsste beim Auslesen raten, welcher Fall vorliegt.
 */
export const CLAIM_TOKEN_KEY = 'radevents.claimToken';

/**
 * Der Weg für jemanden, der aus der Mail kommt und noch kein Konto hat.
 *
 * Führt auf die Registrierung, nicht auf die Anmeldung: wer angeschrieben wurde, hat in aller
 * Regel noch keines. Der Token liegt im Speicher, weil der Weg über die Bestätigungsmail führt
 * und der Rückweg in der Adresse dabei verloren geht.
 */
export const claimTokenPath = (organizerId: number, token: string): string => {
  try { localStorage.setItem(CLAIM_TOKEN_KEY, token); } catch { /* Privatmodus */ }
  const back = encodeURIComponent(`/veranstalter/${organizerId}?freischalten=${encodeURIComponent(token)}`);
  return `/register?redirect=${back}&veranstalter=1`;
};
